import type { SvgIcon } from '@ori-ui/mui';
import { ButtonBase, styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { FC, ReactNode } from 'react';
import { useCallback } from 'react';

import type { BurgerMenuTabNames } from '../../models';
import { UserAvatar } from '../UserSection';

const BurgerMenuTabRoot = styled(ButtonBase)<{ ownerState: { isActive: boolean } }>(({ ownerState, theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.spacing(0, 0, 1, 1),
  color: theme.palette.text.primary,
  display: 'flex',
  fontSize: theme.typography.overline.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  justifyContent: 'flex-start',
  textTransform: 'uppercase',
  width: '50%',

  ...(ownerState.isActive && {
    backgroundColor: theme.palette.common.white,
  }),

  '&::before': {
    fontSize: theme.typography.pxToRem(29),
    margin: theme.spacing(0, 3),
  },
}));

const BurgerMenuTabPrefixWrapper = styled('div')<{ ownerState: { isLoggedIn?: boolean } }>(({ ownerState, theme }) => ({
  borderRadius: '50%',
  display: 'flex',
  fontSize: theme.typography.pxToRem(29),
  height: 29,
  margin: theme.spacing(4, 2),

  ...(ownerState.isLoggedIn && {
    borderRadius: '50%',
    color: theme.palette.common.white,
    display: 'flex',
    fontSize: theme.typography.pxToRem(26),
    height: 26,
  }),
}));

const BurgerMenuTabLabelWrapper = styled('div')(({ theme }) => ({
  marginRight: theme.spacing(3),
  overflow: 'hidden',
  textAlign: 'left',
  textOverflow: 'ellipsis',
  width: '100%',
}));

interface BurgerMenuTabProps {
  activeTab: BurgerMenuTabNames;
  children?: ReactNode;
  tabName: BurgerMenuTabNames;
  tabPrefix: string | typeof SvgIcon;
  onTabClick: (tabName: BurgerMenuTabNames) => void;
  isLoggedIn?: boolean;
}

export const BurgerMenuTab: FC<BurgerMenuTabProps> = ({
  children,
  activeTab,
  tabPrefix,
  tabName,
  onTabClick,
  isLoggedIn,
}) => {
  const clickHandler = useCallback(() => onTabClick(tabName), [onTabClick, tabName]);
  const TabIcon = tabPrefix;
  const ownerState = { isActive: activeTab === tabName, isLoggedIn };
  const { getTestId } = useTestId();

  return (
    <BurgerMenuTabRoot
      disableRipple
      ownerState={ownerState}
      data-testid={getTestId('burger-menu-tab-root')}
      onClick={clickHandler}
    >
      <BurgerMenuTabPrefixWrapper
        ownerState={ownerState}
        data-testid={getTestId('burger-menu-tab-icon-wrapper')}
      >
        {typeof tabPrefix === 'string' ? (
          <UserAvatar data-testid={getTestId('tab-icon')}>{tabPrefix}</UserAvatar>
        ) : (
          <TabIcon
            fontSize="inherit"
            data-testid={getTestId('tab-icon')}
          />
        )}
      </BurgerMenuTabPrefixWrapper>
      <BurgerMenuTabLabelWrapper data-testid={getTestId('burger-menu-tab-label-wrapper')}>
        {children}
      </BurgerMenuTabLabelWrapper>
    </BurgerMenuTabRoot>
  );
};
